/**
 * The sortIds duplicate copies get (CC-1943), so copies keep the Manual (`sortId`) order:
 *  - a copied list's tasks keep the original's order, tied sortIds included (`planListCopySortIds`,
 *    mirrors the backend's `getDuplicateTaskOrder` in `service_checklist` `duplicateLists.ts`);
 *  - a duplicated task lands directly below its original (`planTaskCopySortIds`, ported from admin's
 *    `planDuplicateSortIds` in `checklist-admin` `tasksQueries.ts`).
 * No existing task's sortId changes in either.
 */

/** What a list copy adds to each source sortId (unchanged from before CC-1943, and the backend's value). */
export const LIST_COPY_SORT_ID_INCREMENT = 10;

/** Room a copy of the list's last task leaves under it, and between bottom copies (admin's `INSERTION_DELTA_MS`). */
export const TASK_COPY_SORT_ID_DELTA = 1000;

interface SortableTask {
  taskId: string;
  sortId: number;
}

/**
 * Display order of a list's tasks: sortId numerically, then taskId with plain `<`. Ties come out of Dexie
 * in primary-key (taskId) order, which IndexedDB compares by code unit as `<` does, and `sortBySortId`
 * is stable, so this is the order tied tasks show in.
 */
const compareBySortIdThenTaskId = (a: SortableTask, b: SortableTask): number => {
  const sortIdDifference = Number(a.sortId) - Number(b.sortId);
  if (sortIdDifference !== 0) return sortIdDifference;
  if (a.taskId < b.taskId) return -1;
  if (a.taskId > b.taskId) return 1;
  return 0;
};

/**
 * List duplicate: orders the source tasks by sortId, then taskId, and gives each copy its source's
 * sortId + 10, bumped past the previous copy where source sortIds tie, so every copy gets a distinct
 * sortId in that order. A list with no ties gets exactly sortId + 10 for every copy.
 */
export const planListCopySortIds = <T extends SortableTask>(
  sourceTasks: readonly T[]
): { sourceTask: T; sortId: number }[] => {
  let previousSortId = -Infinity;
  return [...sourceTasks].sort(compareBySortIdThenTaskId).map((sourceTask) => {
    const sortId = Math.max(Number(sourceTask.sortId) + LIST_COPY_SORT_ID_INCREMENT, previousSortId + 1);
    previousSortId = sortId;
    return { sourceTask, sortId };
  });
};

/**
 * Task duplicate (single or bulk, copies in the originals' list): the sortId of each copy, one per
 * original in `originals` order, so it lands directly below its original.
 *
 * `listTasks` is ALL the list's local tasks (a filtered view would hide the real next task).
 * Per original: the copy takes the floor midpoint between it and the next larger sortId, so a copy
 * of a tied task lands after its whole tie group. Copies of selected originals sharing one sortId
 * split that gap evenly in display order. With no larger sortId (the last task) the copy goes at
 * `max(now, original + 1000)`. A gap too small to hold its copies (smaller than copies + 1), or an
 * unusable sortId, falls back to the bottom: `now` + 1000 per copy, after any copies of the last task.
 */
export const planTaskCopySortIds = (
  originals: readonly SortableTask[],
  listTasks: readonly SortableTask[],
  now: number
): number[] => {
  const listSortIds = new Map(listTasks.map((task) => [task.taskId, task.sortId]));
  const values = Array.from(new Set(listTasks.map((task) => Number(task.sortId))))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  // Originals in display order; an original's own list row wins over a stale copy of it.
  const ordered = originals
    .map((original, inputIndex) => ({
      inputIndex,
      taskId: original.taskId,
      sortId: listSortIds.get(original.taskId) ?? original.sortId,
    }))
    .sort(compareBySortIdThenTaskId);

  const planned: number[] = new Array<number>(originals.length);
  const toBottom: number[] = [];
  let endMax: number | null = null;
  for (let start = 0; start < ordered.length; ) {
    const value = Number(ordered[start].sortId);
    let end = start + 1;
    while (end < ordered.length && Number(ordered[end].sortId) === value) end++;
    const group = ordered.slice(start, end);
    start = end;

    if (!Number.isFinite(value)) {
      toBottom.push(...group.map((g) => g.inputIndex));
      continue;
    }
    const next = values.find((v) => v > value);
    if (next === undefined) {
      const base = Math.max(now, value + TASK_COPY_SORT_ID_DELTA);
      group.forEach((g, i) => {
        planned[g.inputIndex] = base + i * TASK_COPY_SORT_ID_DELTA;
      });
      endMax = base + (group.length - 1) * TASK_COPY_SORT_ID_DELTA;
      continue;
    }
    const gap = next - value;
    if (gap < group.length + 1) {
      toBottom.push(...group.map((g) => g.inputIndex));
    } else if (group.length === 1) {
      planned[group[0].inputIndex] = Math.floor((value + next) / 2);
    } else {
      group.forEach((g, i) => {
        planned[g.inputIndex] = value + Math.floor(((i + 1) * gap) / (group.length + 1));
      });
    }
  }

  // Bottom copies follow any copy of the last task so the two never collide.
  const bottomBase = endMax === null ? now : Math.max(now, endMax + TASK_COPY_SORT_ID_DELTA);
  toBottom.forEach((inputIndex, i) => {
    planned[inputIndex] = bottomBase + i * TASK_COPY_SORT_ID_DELTA;
  });
  return planned;
};
